import type { EJournalIssue } from '@/lib/types';
import files from './ejournal.files.json';

// Student's E-Journal, a monthly ITES publication: ONE PDF per month holding
// several articles by ITES members. Each subject is a running column that
// continues the next month (e.g. Piping System Part VIII A in March 2026, Part IX
// in May). Each issue also carries an editorial, Employment News and ITES News.
//
// Volumes run March to February: March is issue 1 of a volume, February is
// issue 12. Checked against the covers (Feb 2025 = Vol 10 Issue 12, Aug 2025 =
// Vol 11 Issue 6, Mar 2026 = Vol 12 Issue 1, May 2026 = Vol 12 Issue 3).
export const EJOURNAL_NAME = "Student's E-Journal";

// Issues that exist online: January 2025 onwards. The earlier ones are not on the
// site yet. Only issues whose PDF the client has sent have a file; the rest
// appear in the archive as "coming soon" until the files arrive.
const FIRST = { year: 2025, month: 1 };
const LAST = { year: 2026, month: 9 };

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const covers = import.meta.glob<string>('@/assets/ejournal/*.jpg', { eager: true, import: 'default' });
const coverFor = (id: string) => Object.entries(covers).find(([path]) => path.endsWith(`/${id}.jpg`))?.[1];

type FileInfo = { pages: number; sizeBytes: number; createdAt: string | null };
const fileInfo = files as Record<string, FileInfo>;

function build(): EJournalIssue[] {
  const out: EJournalIssue[] = [];
  const startIdx = 2025 * 12 + 2; // March 2025 = Vol 11 Issue 1
  for (let y = FIRST.year, m = FIRST.month; y < LAST.year || (y === LAST.year && m <= LAST.month); m === 12 ? (y++, (m = 1)) : m++) {
    const id = `${y}-${String(m).padStart(2, '0')}`;
    const idx = y * 12 + (m - 1);
    const volume = 11 + Math.floor((idx - startIdx) / 12);
    const issue = ((m + 9) % 12) + 1;
    const f = fileInfo[id];
    out.push({
      id, year: y, month: m, volume, issue,
      label: `Vol ${volume} · Issue ${issue}`,
      monthName: MONTHS[m - 1]!,
      // Real creation date of the PDF where we have the file; otherwise the 21st,
      // which is when the issues we have were made. PLACEHOLDER until the CMS has dates.
      publishedAt: f?.createdAt ?? `${id}-21`,
      pdfUrl: f ? `/files/ejournal/${id}.pdf` : undefined,
      cover: f ? coverFor(id) : undefined,
      pages: f?.pages,
      sizeBytes: f?.sizeBytes,
    });
  }
  return out.reverse(); // newest first
}

export const EJOURNAL_ISSUES = build();

/** The newest issue we actually have a file for. */
export const latestAvailableIssue = () => EJOURNAL_ISSUES.find((i) => i.pdfUrl);

/** The newest few issues that have a file, after the latest one (for the Home card). */
export const earlierIssues = (n: number) => {
  const latest = latestAvailableIssue();
  return EJOURNAL_ISSUES.filter((i) => i.pdfUrl && i !== latest).slice(0, n);
};

/** How many newer issues exist on the calendar but have no file yet. */
export const missingNewerIssues = () => {
  const latest = latestAvailableIssue();
  return latest ? EJOURNAL_ISSUES.indexOf(latest) : EJOURNAL_ISSUES.length;
};

export function issuesByYear(): { year: number; items: EJournalIssue[] }[] {
  const years = [...new Set(EJOURNAL_ISSUES.map((i) => i.year))];
  return years.map((year) => ({ year, items: EJOURNAL_ISSUES.filter((i) => i.year === year) }));
}
